import type {
  AiModelEnum,
  BuyerLoadSampleWithBreakdown,
  EvidenceUploadMode,
  PaginatedEvidenceList,
  QualityReportAiGravimetricComposition,
  SampleVariant,
  SiteMaterialSampleWithBreakdown,
} from 'api';
import { formatDate } from 'date';
import { useState } from 'react';
import { MediaDialog, type PhotoEvidenceItem, PhotoEvidenceList, Stack, Typography, TypographyIcon } from 'ui';
import { CalendarMonth, Notes, Person, Scale } from 'ui/icons';

import { AiSampleBreakdown, MaterialSampleBreakdown } from '../../../breakdowns';
import { AnnotatedMediaItem, useAnnotations } from '../../../quality-report';
import { IGNORE_BALE_LABELS } from '../../../quality-report/constants';
import { useOfflineEvidence } from '../../hooks/useOfflineEvidence';
import { formatSampleEvidence } from '../../utils/formatSampleEvidence';
import { AiFilters } from '../AiFilters/AiFilters';
import { OfflineEvidenceGallery } from '../OfflineEvidenceGallery/OfflineEvidenceGallery';

export const SampleBreakdown = ({
  isMobile,
  displayManualBreakdown = true,
  sample,
  model,
  evidence,
  variant,
  isOnline,
  mode,
  parentId,
  onSuccessfulEvidenceSync,
}: {
  isMobile: boolean;
  sample: SiteMaterialSampleWithBreakdown | BuyerLoadSampleWithBreakdown;
  model?: AiModelEnum;
  displayManualBreakdown?: boolean;
  evidence: PaginatedEvidenceList;
  variant: SampleVariant;
  isOnline: boolean;
  mode: EvidenceUploadMode;
  parentId?: string | number;
  onSuccessfulEvidenceSync?: () => void;
}) => {
  const [selectedOnlineImage, setSelectedOnlineImage] = useState<string>('');
  const formattedEvidence = formatSampleEvidence(evidence.results);

  const { offlinePhotos } = useOfflineEvidence({
    isOnline,
    mode,
    parentId: parentId ?? sample.id,
  });

  const onlineCarouselMedia = evidence.results.map((file) => String(file.file));

  const {
    selectedAnnotation,
    handleImageChange,
    hiddenCategories,
    handleHideCategory,
    handleToggleLegend,
    showLegend,
    showSidebar,
  } = useAnnotations({});

  const showSwitch = Boolean(selectedAnnotation?.some(({ label }) => !IGNORE_BALE_LABELS.includes(label)));

  const handleSelectOnline = (src: string) => {
    const relatedMedia = formattedEvidence.find((photo) => photo.file === src || photo.preview === src);
    setSelectedOnlineImage(relatedMedia?.file || '');
    handleImageChange(relatedMedia?.annotations);
  };

  return (
    <>
      <Stack direction={isMobile ? 'column' : 'row'} gap={3}>
        <Stack gap={2} width={isMobile ? '100%' : '50%'}>
          <Stack gap={1}>
            <Typography variant="body1" fontWeight="bold">
              Sample
            </Typography>
            <Stack gap={2} direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
              <TypographyIcon size="small" variant="body2" icon={CalendarMonth}>
                {formatDate(sample.date)}
              </TypographyIcon>
              {sample.user?.full_name ? (
                <TypographyIcon size="small" variant="body2" icon={Person}>
                  {sample.user?.full_name}
                </TypographyIcon>
              ) : null}
              <TypographyIcon size="small" variant="body2" icon={Scale}>
                {sample.total_weight}kg
              </TypographyIcon>
              <TypographyIcon size="small" variant="body2" icon={Notes}>
                {sample.description || 'No Notes'}
              </TypographyIcon>
            </Stack>
          </Stack>
          <Stack gap={5}>
            {displayManualBreakdown ? (
              <Stack gap={1}>
                <Typography variant="body1" fontWeight="bold">
                  Manual Breakdown
                </Typography>
                <MaterialSampleBreakdown
                  variant={variant}
                  items={sample.breakdown.map((item) => ({
                    label: item.name_display,
                    unit: item.unit,
                    unit_display: item.unit_display,
                    value: item.value,
                  }))}
                />
              </Stack>
            ) : null}

            {'average_features' in sample && sample.average_features && model ? (
              <Stack gap={1}>
                <Typography variant="body1" fontWeight="bold">
                  AI Breakdown
                </Typography>
                <AiSampleBreakdown
                  variant={model}
                  composition={sample.average_features as QualityReportAiGravimetricComposition}
                  totalWeight={Number(sample.total_weight)}
                />
              </Stack>
            ) : null}
          </Stack>
        </Stack>

        <Stack gap={1} width={isMobile ? '100%' : '50%'}>
          <Typography variant="body1" fontWeight="bold">
            Sample Evidence
          </Typography>
          <PhotoEvidenceList
            openOnClick
            slotProps={{
              imageList: {
                cols: isMobile ? 3 : 5,
              },
            }}
            evidencePhotos={evidence.results.map(({ file, preview }) => ({
              src: (preview || file) ?? '',
            }))}
            handleClick={(photo: PhotoEvidenceItem) => handleSelectOnline(photo.src)}
          />

          {offlinePhotos.length > 0 ? (
            <OfflineEvidenceGallery
              isOnline={isOnline}
              parentId={String(parentId ?? sample.id)}
              mode={mode}
              onSyncComplete={onSuccessfulEvidenceSync}
            />
          ) : null}
        </Stack>
      </Stack>

      <MediaDialog
        open={Boolean(selectedOnlineImage)}
        selectedMedium={selectedOnlineImage}
        onClose={() => setSelectedOnlineImage('')}
        media={onlineCarouselMedia}
        handleNavigate={handleSelectOnline}
        slotProps={{
          imageCarousel: {
            slotProps: {
              ...(selectedAnnotation && model
                ? {
                    mediaItemSlot: (
                      <AnnotatedMediaItem
                        image={selectedOnlineImage}
                        annotations={selectedAnnotation}
                        hiddenCategories={hiddenCategories}
                        showLegend={showLegend}
                        model={model}
                      />
                    ),
                    sidebar: showSidebar
                      ? {
                          children: (
                            <AiFilters
                              selectedAnnotation={selectedAnnotation}
                              hiddenCategories={hiddenCategories}
                              handleHideCategory={handleHideCategory}
                              handleToggleLegend={handleToggleLegend}
                              showLegend={showLegend}
                              showSwitch={showSwitch}
                              model={model}
                              showAerosols
                            />
                          ),
                          label: 'Adjust AI Filters',
                        }
                      : undefined,
                  }
                : null),
            },
          },
        }}
      />
    </>
  );
};
